/**
 * StreamX `/streamx/pricing` — Premium Broadcasting Membership.
 * StreamX Sprint 3c rebuild.
 * ------------------------------------------------------------------
 * Doctrine:
 *   • Obsidian #0A0A0A · glass cards (white/[0.02] + white/[0.08])
 *   • Annual/Monthly toggle with amber 17% savings ribbon
 *   • Pilot card (free) vs. Pro card (gold-accented, scale-105, amber glow)
 *   • Feature comparison strip — glass rows, no SaaS tables
 *   • Testimonials carousel (3 cards)
 *   • FAQ accordion (cinematic disclosure)
 *
 * Behaviour preserved verbatim:
 *   • GET /api/livex/plans
 *   • Pricing math (monthly/yearly with GST in paise)
 *   • CTA routing for authenticated / unauthenticated streamers
 */
import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Check, X, ArrowLeft, Sparkles, Radio, Building2, Shield, Zap, BadgeCheck,
  Crown, ChevronDown, Star, Trophy,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { STREAMX_BRAND } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AMBER = '#FFB800';

function formatINR(v) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v);
}

function Anim({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={v ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ── Pricing tier card ────────────────────────────────────────────────

function TierCard({ tier, kind, isYearly, gstPct, ctaHref, ctaLabel }) {
  const isPro = kind === 'pro';
  const price = isPro
    ? (isYearly ? tier.yearly_inr : tier.monthly_inr)
    : 0;
  const totalPaise = isPro
    ? (isYearly ? tier.yearly_inr_with_gst_paise : tier.monthly_inr_with_gst_paise)
    : 0;
  const cycleLabel = isYearly ? '/year' : '/month';

  return (
    <div
      data-testid={`streamx-pricing-${kind}`}
      className={`relative flex flex-col p-7 sm:p-9 rounded-3xl transition-all duration-700 ${
        isPro ? 'md:scale-[1.04] md:-translate-y-2' : ''
      }`}
      style={{
        background: isPro
          ? 'linear-gradient(155deg, rgba(255,184,0,0.06) 0%, rgba(255,59,48,0.04) 50%, rgba(255,255,255,0.02) 100%)'
          : 'rgba(255,255,255,0.02)',
        border: isPro
          ? `1px solid rgba(255,184,0,0.35)`
          : '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
        boxShadow: isPro ? '0 24px 64px rgba(255,184,0,0.10), 0 0 0 1px rgba(255,184,0,0.10)' : 'none',
      }}
    >
      {/* Pro flag */}
      {isPro && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <div
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] text-[#0A0A0A]"
            style={{
              background: `linear-gradient(135deg, ${AMBER} 0%, #FF8A00 100%)`,
              boxShadow: `0 8px 24px rgba(255,184,0,0.45)`,
            }}
          >
            <Crown className="h-3 w-3" /> Members' Choice
          </div>
        </div>
      )}

      {/* Tier heading */}
      <div className="text-[10px] font-bold tracking-[0.3em] uppercase mb-3"
        style={{ color: isPro ? AMBER : 'rgba(255,255,255,0.45)' }}
      >
        {isPro ? 'FOR SERIOUS ACADEMIES' : 'FREE FOREVER'}
      </div>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">{tier.name}</h2>
      <p className="mt-2 text-sm text-white/55 font-medium leading-relaxed">{tier.tagline}</p>

      {/* Price */}
      <div className="mt-7 pb-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="flex items-baseline gap-2">
          <span className="text-5xl font-bold tracking-tight text-white tabular-nums">
            {isPro ? `₹${formatINR(price)}` : '₹0'}
          </span>
          <span className="text-sm text-white/55 font-medium">{isPro ? cycleLabel : '/forever'}</span>
        </div>
        {isPro ? (
          <div className="mt-2 text-[11px] font-medium text-white/45">
            + {gstPct}% GST = ₹{(totalPaise / 100).toFixed(2)} {isYearly ? '/yr' : '/mo'}
            {isYearly && tier.yearly_savings_percent && (
              <span className="ml-2 inline-flex items-center gap-1" style={{ color: AMBER }}>
                <Sparkles className="h-3 w-3" /> Save {tier.yearly_savings_percent}% vs monthly
              </span>
            )}
          </div>
        ) : (
          <div className="mt-2 text-[11px] font-medium text-white/45">
            No credit card · No trial limits
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="mt-6 space-y-3 flex-1">
        {(tier.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-white/80 font-medium leading-relaxed">
            <span
              className="flex-shrink-0 mt-0.5 h-4 w-4 rounded-full grid place-items-center"
              style={{
                background: isPro ? `${AMBER}1c` : 'rgba(255,59,48,0.14)',
                border: isPro ? `1px solid ${AMBER}55` : '1px solid rgba(255,59,48,0.35)',
              }}
            >
              <Check className="h-2.5 w-2.5" style={{ color: isPro ? AMBER : '#FF6B5C' }} />
            </span>
            {f}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Link
        to={ctaHref}
        data-testid={`streamx-pricing-${kind}-cta`}
        className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-500"
        style={{
          background: isPro
            ? `linear-gradient(135deg, ${AMBER} 0%, #FF8A00 100%)`
            : 'rgba(255,255,255,0.04)',
          color: isPro ? '#0A0A0A' : 'white',
          border: isPro ? 'none' : '1px solid rgba(255,255,255,0.10)',
          boxShadow: isPro ? `0 12px 32px rgba(255,184,0,0.35)` : 'none',
        }}
      >
        {ctaLabel}
      </Link>
    </div>
  );
}

// ── Cycle toggle ─────────────────────────────────────────────────────

function CycleToggle({ cycle, setCycle }) {
  return (
    <div
      className="inline-flex items-center gap-1 p-1 rounded-full"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
      }}
      data-testid="streamx-pricing-cycle"
    >
      {['monthly', 'yearly'].map((c) => {
        const sel = cycle === c;
        return (
          <button
            key={c}
            onClick={() => setCycle(c)}
            data-testid={`streamx-pricing-cycle-${c}`}
            aria-pressed={sel}
            className={`relative px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-[0.12em] transition-all duration-500 ${
              c === 'yearly' ? 'pr-12' : ''
            }`}
            style={{
              background: sel ? 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)' : 'transparent',
              color: sel ? 'white' : 'rgba(255,255,255,0.55)',
            }}
          >
            {c === 'monthly' ? 'Monthly' : 'Annual'}
            {c === 'yearly' && (
              <span
                className="absolute top-1/2 right-1.5 -translate-y-1/2 text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                style={{
                  background: `${AMBER}24`,
                  color: AMBER,
                  border: `1px solid ${AMBER}55`,
                }}
              >
                −17%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Comparison strip (NO table) ──────────────────────────────────────

const COMPARISON_ROWS = [
  ['Live streams / month',          '2',         'Unlimited'],
  ['Concurrent viewers',            'Up to 100', 'Unlimited'],
  ['Live chat + reactions',         true,        true],
  ['Mark Highlight chapters',       true,        true],
  ['Academy brand page (SEO)',      true,        true],
  ['Recording & replay archive',    false,       'Soon'],
  ['Multi-platform simulcast',      false,       'Soon'],
  ['AI highlight reels',            false,       'Phase 2'],
  ['"Powered by StreamX" watermark', 'Required', 'Removed'],
  ['Priority broadcasting support', false,       true],
  ['GST-invoiced billing',          false,       true],
];

function ComparisonStrip() {
  return (
    <div
      className="rounded-3xl overflow-hidden"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
      }}
      data-testid="streamx-pricing-comparison"
    >
      <div className="grid grid-cols-[1.7fr_1fr_1fr] gap-0 px-5 sm:px-7 py-4"
        style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="text-[10px] font-bold tracking-[0.22em] text-white/45 uppercase">Feature</div>
        <div className="text-[10px] font-bold tracking-[0.22em] text-white/55 uppercase text-center">Free Pilot</div>
        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-center" style={{ color: AMBER }}>StreamX Pro</div>
      </div>
      {COMPARISON_ROWS.map(([label, pilot, pro], i) => (
        <div
          key={i}
          className="grid grid-cols-[1.7fr_1fr_1fr] gap-0 px-5 sm:px-7 py-4 text-sm transition-colors hover:bg-white/[0.02]"
          style={i < COMPARISON_ROWS.length - 1 ? { borderBottom: '1px solid rgba(255,255,255,0.04)' } : {}}
        >
          <div className="text-white/90 font-medium leading-snug">{label}</div>
          <div className="text-center text-white/55 font-medium flex items-center justify-center">
            {pilot === true ? <Check className="h-4 w-4" style={{ color: '#6ee7b7' }} /> :
             pilot === false ? <X className="h-4 w-4 text-white/25" /> : <span>{pilot}</span>}
          </div>
          <div className="text-center text-white font-semibold flex items-center justify-center">
            {pro === true ? <Check className="h-4 w-4" style={{ color: AMBER }} /> :
             pro === false ? <X className="h-4 w-4 text-white/25" /> :
             <span style={{
               color: pro === 'Removed' ? '#6ee7b7' :
                      (pro === 'Soon' || pro === 'Phase 2') ? AMBER :
                      'white',
             }}>{pro}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Testimonials ─────────────────────────────────────────────────────

const TESTIMONIALS = [
  {
    quote: 'Parents started watching the matches from home. The academy feels bigger overnight.',
    author: 'Founder',
    academy: 'Wadekar Football Academy',
  },
  {
    quote: 'Our highlight reels are now shared 10× more — every parent reposts on WhatsApp.',
    author: 'Head Coach',
    academy: 'Spartan Cricket Academy',
  },
  {
    quote: 'The brand page lifted enrollment. New parents now ask “can we be on StreamX?”',
    author: 'Director',
    academy: 'Elite Basketball Center',
  },
];

function TestimonialsRow() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="streamx-pricing-testimonials">
      {TESTIMONIALS.map((t, i) => (
        <Anim key={i} delay={i * 0.08}>
          <div
            className="p-6 rounded-2xl h-full flex flex-col"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(24px)',
            }}
          >
            <div className="flex items-center gap-0.5 mb-4">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} className="h-3.5 w-3.5" style={{ color: AMBER, fill: AMBER }} />
              ))}
            </div>
            <p className="text-white/85 leading-relaxed flex-1 font-medium">
              "{t.quote}"
            </p>
            <div className="mt-5 pt-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <div className="text-sm font-semibold text-white">{t.author}</div>
              <div className="text-xs text-white/45 font-medium">{t.academy}</div>
            </div>
          </div>
        </Anim>
      ))}
    </div>
  );
}

// ── FAQ accordion ────────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    q: 'Can I cancel anytime?',
    a: 'Yes — monthly plans cancel at the next billing cycle. Annual plans qualify for a full refund within 14 days, no questions asked.',
  },
  {
    q: 'How does Razorpay billing work?',
    a: 'Pro upgrades are processed via Razorpay with GST-compliant invoices. Until your account is on a paid plan, the Free Pilot tier stays unlocked forever — no card required.',
  },
  {
    q: 'Is my data portable?',
    a: 'Yes. Export your full broadcast history, highlight markers, and viewership analytics anytime as JSON or CSV. Your archive belongs to your academy.',
  },
  {
    q: 'What happens to my replays if I downgrade?',
    a: 'Existing replays stay accessible. New replays follow the new plan\'s retention policy. We never delete past matches without explicit consent.',
  },
  {
    q: 'Do you offer custom enterprise plans?',
    a: 'Yes — multi-academy networks, white-label streaming, and integrated school-system deployments are available. Email founders@out-play.in to discuss.',
  },
];

function FAQItem({ item, isOpen, onToggle, idx }) {
  return (
    <div
      className="rounded-2xl overflow-hidden transition-all duration-500"
      style={{
        background: isOpen ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
      }}
      data-testid={`streamx-pricing-faq-item-${idx}`}
    >
      <button
        onClick={onToggle}
        data-testid={`streamx-pricing-faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left transition-colors"
      >
        <span className="text-base font-semibold text-white tracking-tight pr-4">
          {item.q}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="flex-shrink-0"
        >
          <ChevronDown className="h-4 w-4 text-white/55" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 pt-1 text-sm text-white/70 font-medium leading-relaxed">
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FAQAccordion() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="space-y-3" data-testid="streamx-pricing-faq">
      {FAQ_ITEMS.map((item, i) => (
        <FAQItem
          key={i}
          item={item}
          idx={i}
          isOpen={openIdx === i}
          onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
        />
      ))}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

export default function LivexPricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [cycle, setCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/livex/plans`)
      .then((r) => setPlans(r.data))
      .catch(() => { /* fall back to defaults */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = `Pricing — ${STREAMX_BRAND} Broadcasting Membership | Out-Play`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
    m.setAttribute('content',
      `${STREAMX_BRAND} Pro at ₹299/month + GST. Unlimited live streams, recording, multi-platform simulcast, brand pages. Free Pilot tier available.`);
  }, []);

  const isStreamer = ['academy_owner', 'super_admin', 'coach', 'employee'].includes(user?.role || '');
  const proCtaHref = user ? '/streamx/subscription' : `/login?redirect=${encodeURIComponent('/streamx/subscription')}`;
  const pilotCtaHref = user ? '/streamx/studio' : `/login?redirect=${encodeURIComponent('/streamx/studio')}`;

  if (loading || !plans) {
    return (
      <div className="min-h-screen grid place-items-center text-white" style={{ background: '#0A0A0A' }}>
        <motion.div
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
          className="text-sm font-medium text-white/55"
        >
          Loading membership…
        </motion.div>
      </div>
    );
  }

  const pilot = plans.plans.find((p) => p.id === 'pilot');
  const pro = plans.plans.find((p) => p.id === 'pro');

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: '#0A0A0A' }}
      data-testid="streamx-pricing-page"
    >
      {/* Top bar */}
      <header
        className="sticky top-0 z-40 border-b"
        style={{
          background: 'rgba(0,0,0,0.65)',
          borderColor: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => navigate('/streamx')}
            data-testid="streamx-pricing-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to {STREAMX_BRAND}
          </button>
          <div className="text-[10px] tracking-[0.32em] font-bold text-white/45 uppercase">
            {STREAMX_BRAND} · MEMBERSHIP
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              `radial-gradient(45% 35% at 50% 0%, ${AMBER}1f 0%, transparent 60%),` +
              'radial-gradient(40% 30% at 20% 60%, rgba(255,59,48,0.10) 0%, transparent 60%),' +
              '#0A0A0A',
          }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-16 text-center">
          <Anim>
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.22em]"
              style={{
                background: `${AMBER}10`,
                border: `1px solid ${AMBER}40`,
                color: AMBER,
                backdropFilter: 'blur(12px)',
              }}
            >
              <Trophy className="h-3.5 w-3.5" /> Broadcasting Membership
            </div>
            <h1 className="mt-7 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.02]" data-testid="streamx-pricing-headline">
              Membership built for{' '}
              <span
                style={{
                  background: `linear-gradient(120deg, ${AMBER} 0%, #FF8A00 50%, #FF3B30 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                serious academies.
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/55 max-w-2xl mx-auto leading-relaxed font-medium">
              Start free. Upgrade when your matches start filling the stands.
              Cancel anytime. 14-day refund on annual plans, no questions asked.
            </p>
            <div className="mt-9 flex justify-center">
              <CycleToggle cycle={cycle} setCycle={setCycle} />
            </div>
            {cycle === 'yearly' && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium"
                style={{ color: AMBER }}
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                Save ₹589/year · 2 months free vs monthly
              </motion.div>
            )}
          </Anim>
        </div>
      </section>

      {/* Social proof bar */}
      <section
        className="border-y"
        style={{
          background: 'rgba(0,0,0,0.3)',
          borderColor: 'rgba(255,255,255,0.06)',
        }}
        data-testid="streamx-pricing-social-proof"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-7 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          {[
            { icon: Building2, label: 'Academies onboarded', value: '12+' },
            { icon: Radio, label: 'Hours streamed', value: '300+' },
            { icon: Shield, label: 'Uptime', value: '99.9%' },
            { icon: Zap, label: 'Setup time', value: '60 sec' },
          ].map((s, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <s.icon className="h-4 w-4 text-white/40" />
              <div className="text-xl sm:text-2xl font-bold tracking-tight text-white tabular-nums">{s.value}</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-12 sm:pb-16">
        <div className="grid sm:grid-cols-2 gap-5 sm:gap-7 max-w-4xl mx-auto">
          <Anim>
            <TierCard
              tier={pilot}
              kind="pilot"
              isYearly={cycle === 'yearly'}
              gstPct={plans.gst_percent}
              ctaHref={pilotCtaHref}
              ctaLabel={pilot.cta || 'Start the Free Pilot'}
            />
          </Anim>
          <Anim delay={0.1}>
            <TierCard
              tier={pro}
              kind="pro"
              isYearly={cycle === 'yearly'}
              gstPct={plans.gst_percent}
              ctaHref={proCtaHref}
              ctaLabel={isStreamer ? 'Manage Subscription' : (pro.cta || 'Upgrade to Pro')}
            />
          </Anim>
        </div>

        {/* Comparison */}
        <section className="mt-20 sm:mt-24">
          <Anim>
            <div className="text-center mb-9">
              <div className="text-[10px] font-bold tracking-[0.32em] text-white/45 uppercase mb-3">
                Feature Comparison
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Everything you get, side by side.
              </h2>
            </div>
          </Anim>
          <Anim>
            <ComparisonStrip />
          </Anim>
        </section>

        {/* Testimonials */}
        <section className="mt-20 sm:mt-24">
          <Anim>
            <div className="text-center mb-9">
              <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-3" style={{ color: AMBER }}>
                What Members Say
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Built by academies, for academies.
              </h2>
            </div>
          </Anim>
          <TestimonialsRow />
        </section>

        {/* FAQ */}
        <section className="mt-20 sm:mt-24 max-w-3xl mx-auto">
          <Anim>
            <div className="text-center mb-9">
              <div className="text-[10px] font-bold tracking-[0.32em] text-white/45 uppercase mb-3">
                Frequently Asked
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Answers, on the record.
              </h2>
            </div>
          </Anim>
          <FAQAccordion />
        </section>

        {/* Final CTA */}
        <section className="mt-20 sm:mt-24">
          <Anim>
            <div
              className="relative overflow-hidden rounded-3xl p-10 sm:p-14 text-center"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${AMBER}30`,
                backdropFilter: 'blur(24px)',
              }}
            >
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  background: `radial-gradient(60% 60% at 50% 100%, ${AMBER}40 0%, transparent 60%)`,
                }}
              />
              <div className="relative">
                <h3 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                  Ready to broadcast your academy?
                </h3>
                <p className="mt-4 text-base text-white/55 font-medium max-w-lg mx-auto">
                  Start with the Free Pilot. Upgrade when your matches start drawing real crowds.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-md mx-auto">
                  <Link
                    to={pilotCtaHref}
                    data-testid="streamx-pricing-final-cta-pilot"
                    className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-base font-semibold text-white transition-all duration-500"
                    style={{
                      background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)',
                      boxShadow: '0 12px 32px rgba(255,59,48,0.35)',
                    }}
                  >
                    <Radio className="h-4 w-4" /> Start Free Pilot
                  </Link>
                  <Link
                    to="/streamx/live"
                    data-testid="streamx-pricing-final-cta-watch"
                    className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-base font-semibold text-white/85 hover:text-white transition-all duration-500"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.10)',
                    }}
                  >
                    Watch live matches
                  </Link>
                </div>
              </div>
            </div>
          </Anim>
        </section>
      </main>

      {/* Footer */}
      <section
        className="border-t"
        style={{ background: 'rgba(0,0,0,0.4)', borderColor: 'rgba(255,255,255,0.06)' }}
        data-testid="streamx-pricing-footer"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-between gap-3 flex-wrap text-[12px] font-medium text-white/45">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" />
            {STREAMX_BRAND} · Sports broadcasting for academies
          </div>
          <div className="flex items-center gap-5">
            <Link to="/streamx/live" className="hover:text-white transition-colors">Live</Link>
            <Link to="/streamx/replays" className="hover:text-white transition-colors">Replays</Link>
            <Link to="/streamx/stories" className="hover:text-white transition-colors">Stories</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
